// src/components/Atelier3/Activite4/MeasureForm.jsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import api from '../../../api/apiClient';
import workshop3Service from '../../../services/workshop3Service'; // Use hydration service

const MeasureForm = ({ measure, onSave, onCancel, analysisId }) => {
  const { t } = useTranslation();

  const CATEGORIES = [
    { id: 'governance', label: t('workshop4.operationalScenarios.measureForm.options.categories.governance') },
    { id: 'protection', label: t('workshop4.operationalScenarios.measureForm.options.categories.protection') },
    { id: 'defense', label: t('workshop4.operationalScenarios.measureForm.options.categories.defense') },
    { id: 'resilience', label: t('workshop4.operationalScenarios.measureForm.options.categories.resilience') },
    { id: 'other', label: t('workshop4.operationalScenarios.measureForm.options.categories.other') }
  ];

  const PRIORITIES = [
    { id: 'high', label: t('workshop4.operationalScenarios.measureForm.options.priorities.high'), color: 'bg-red-100 text-red-800 border-red-200' },
    { id: 'medium', label: t('workshop4.operationalScenarios.measureForm.options.priorities.medium'), color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { id: 'low', label: t('workshop4.operationalScenarios.measureForm.options.priorities.low'), color: 'bg-green-100 text-green-800 border-green-200' }
  ];

  const IMPLEMENTATION_STATUSES = [
    { id: 'planned', label: t('workshop4.operationalScenarios.measureForm.options.statuses.planned') },
    { id: 'in-progress', label: t('workshop4.operationalScenarios.measureForm.options.statuses.inProgress') },
    { id: 'implemented', label: t('workshop4.operationalScenarios.measureForm.options.statuses.implemented') },
    { id: 'abandoned', label: t('workshop4.operationalScenarios.measureForm.options.statuses.abandoned') }
  ];

  const IMPACT_AREAS = [
    { id: 'confidentiality', label: t('workshop4.operationalScenarios.measureForm.options.impactAreas.confidentiality') },
    { id: 'integrity', label: t('workshop4.operationalScenarios.measureForm.options.impactAreas.integrity') },
    { id: 'availability', label: t('workshop4.operationalScenarios.measureForm.options.impactAreas.availability') },
    { id: 'traceability', label: t('workshop4.operationalScenarios.measureForm.options.impactAreas.traceability') },
    { id: 'resilience', label: t('workshop4.operationalScenarios.measureForm.options.impactAreas.resilience') }
  ];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'governance',
    stakeholders: [],
    attackPaths: [],
    sourceRisks: [], // Added field for risk sources
    objectifsVises: [], // Added field for target objectives
    priority: 'medium',
    implementation: 'planned',
    responsibleParty: '',
    deadline: '',
    cost: 'medium',
    dependanceEffectiveness: 0,
    penetrationEffectiveness: 0,
    maturiteCyberEffectiveness: 0,
    confianceEffectiveness: 0,
    notes: ''
  });

  const [stakeholderOptions, setStakeholderOptions] = useState([]);
  const [attackPathOptions, setAttackPathOptions] = useState([]);
  const [sourceRiskOptions, setSourceRiskOptions] = useState([]);
  const [objectifViseOptions, setObjectifViseOptions] = useState([]);
  const atelier1Store = useAtelier1Store();
  const currentContextData = atelier1Store.contextData;
  const responsiblePersonsList = currentContextData?.responsiblePersonsList || [];

  const [showDirectPaths, setShowDirectPaths] = useState(true);

  // Load data and initialize form
  useEffect(() => {
    if (analysisId) {
      loadData();
    }

    if (measure) {
      setFormData({
        ...formData,
        ...measure
      });


    }
  }, [analysisId, measure, responsiblePersonsList]);

  const loadData = async () => {
    try {
      // Load stakeholders
      const stakeholdersResponse = await api.get(`/analyses/${analysisId}/atelier3/stakeholders`);
      if (stakeholdersResponse.success && stakeholdersResponse.data) {
        setStakeholderOptions(stakeholdersResponse.data);
      }

      // Load attack paths from hydration service — only retained (like W4 Activity 1)
      console.log('🔄 Loading hydrated attack paths for measure form (retained only)...');
      const pathsResponse = await workshop3Service.getAttackPaths(analysisId, true);
      if (pathsResponse.success && pathsResponse.data) {
        // Backend already filters retainedOnly, but keep client-side safety for stale data / backward compat
        const retainedPaths = Array.isArray(pathsResponse.data)
          ? pathsResponse.data.filter((p) => p.retained !== false)
          : [];
        // Process attack paths to include reference codes if not present
        const processedPaths = retainedPaths.map((path, index) => ({
          ...path,
          referenceCode: path.referenceCode || `CA${String(index + 1).padStart(2, '0')}`
        }));

        console.log('✅ Loaded', processedPaths.length, 'retained attack paths (filtered)');
        setAttackPathOptions(processedPaths);
      }

      try {
        const sourcesResponse = await api.get(`/analyses/${analysisId}/atelier2/sources-risque`);
        if (sourcesResponse.success && sourcesResponse.data) {
          setSourceRiskOptions(sourcesResponse.data);
        }
      } catch (error) {
        console.error('Error loading sources de risque:', error);
      }

      // Load objectifs visés
      const objectifsResponse = await api.get(`/analyses/${analysisId}/atelier2/objectifs-vises`);
      if (objectifsResponse.success && objectifsResponse.data) {
        setObjectifViseOptions(objectifsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data for measure form:', error);
    }
  };

  const clampEffectiveness = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    return Math.max(0, Math.min(4, num));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.endsWith('Effectiveness')) {
      const clamped = clampEffectiveness(value);
      setFormData(prev => ({
        ...prev,
        [name]: clamped
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  const handleStakeholderChange = (e) => {
    const stakeholderId = e.target.value;
    if (e.target.checked) {
      setFormData(prev => ({
        ...prev,
        stakeholders: [...prev.stakeholders, stakeholderId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        stakeholders: prev.stakeholders.filter(id => id !== stakeholderId)
      }));
    }
  };

  const handleAttackPathToggle = (pathId, isChecked) => {
    const selectedPath = attackPathOptions.find(path => path.id === pathId);

    if (isChecked) {
      setFormData(prev => {
        const updatedAttackPaths = [...prev.attackPaths, pathId];
        let updatedSourceRisks = [...prev.sourceRisks];
        if (selectedPath && selectedPath.sourceRiskId && !updatedSourceRisks.includes(selectedPath.sourceRiskId)) {
          updatedSourceRisks.push(selectedPath.sourceRiskId);
        }
        let updatedObjectifsVises = [...prev.objectifsVises];
        if (selectedPath && selectedPath.objectifVise && !updatedObjectifsVises.includes(selectedPath.objectifVise)) {
          updatedObjectifsVises.push(selectedPath.objectifVise);
        }
        return {
          ...prev,
          attackPaths: updatedAttackPaths,
          sourceRisks: updatedSourceRisks,
          objectifsVises: updatedObjectifsVises
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        attackPaths: prev.attackPaths.filter(id => id !== pathId)
      }));
    }
  };

  const handleAttackPathChange = (e) => {
    handleAttackPathToggle(e.target.value, e.target.checked);
  };

  const handleSourceRiskChange = (e) => {
    const sourceId = e.target.value;
    if (e.target.checked) {
      setFormData(prev => ({
        ...prev,
        sourceRisks: [...prev.sourceRisks, sourceId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        sourceRisks: prev.sourceRisks.filter(id => id !== sourceId)
      }));
    }
  };

  const handleObjectifViseChange = (e) => {
    const objectifId = e.target.value;
    if (e.target.checked) {
      setFormData(prev => ({
        ...prev,
        objectifsVises: [...prev.objectifsVises, objectifId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        objectifsVises: prev.objectifsVises.filter(id => id !== objectifId)
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      dependanceEffectiveness: clampEffectiveness(formData.dependanceEffectiveness),
      penetrationEffectiveness: clampEffectiveness(formData.penetrationEffectiveness),
      maturiteCyberEffectiveness: clampEffectiveness(formData.maturiteCyberEffectiveness),
      confianceEffectiveness: clampEffectiveness(formData.confianceEffectiveness),
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'governance',
      stakeholders: [],
      attackPaths: [],
      sourceRisks: [],
      objectifsVises: [],
      priority: 'medium',
      implementation: 'planned',
      responsibleParty: '',
      deadline: '',
      cost: 'medium',
      dependanceEffectiveness: 0,
      penetrationEffectiveness: 0,
      maturiteCyberEffectiveness: 0,
      confianceEffectiveness: 0,
      notes: '',
    });
  };

  const handleSaveAndAddAnother = () => {
    onSave({
      ...formData,
      dependanceEffectiveness: clampEffectiveness(formData.dependanceEffectiveness),
      penetrationEffectiveness: clampEffectiveness(formData.penetrationEffectiveness),
      maturiteCyberEffectiveness: clampEffectiveness(formData.maturiteCyberEffectiveness),
      confianceEffectiveness: clampEffectiveness(formData.confianceEffectiveness),
    }, true);
    resetForm();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-base font-semibold text-white">
              {measure
                ? t('workshop4.operationalScenarios.measureForm.titleEdit')
                : t('workshop4.operationalScenarios.measureForm.titleNew')}
            </h3>
            <p className="text-blue-100 text-xs mt-0.5">
              {measure
                ? t('workshop4.operationalScenarios.measureForm.subtitleEdit')
                : t('workshop4.operationalScenarios.measureForm.subtitleNew')}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-blue-100 hover:text-white hover:bg-blue-700 p-2 rounded-lg transition-colors duration-150"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-3">
        {/* Section 1: Chemins d'attaque */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-1.5"></div>
              {t('workshop4.operationalScenarios.measureForm.sections.attackPaths')}
            </h4>
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showDirectPaths}
                onChange={(e) => setShowDirectPaths(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded"
              />
              {t('workshop4.operationalScenarios.measureForm.attackPaths.showDirect')}
            </label>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-2 max-h-60 overflow-y-auto">
            {attackPathOptions.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500">{t('workshop4.operationalScenarios.measureForm.attackPaths.emptyTitle')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attackPathOptions
                  .filter(path => showDirectPaths || path.type !== 'direct')
                  .map(path => {
                  const sourceRisk = sourceRiskOptions.find(s => s.id === path.sourceRiskId);
                  const sourceName = sourceRisk ?
                    (sourceRisk.description || sourceRisk.name) :
                    (path.sourceRiskDescription || path.sourceRiskName || t('workshop4.operationalScenarios.measureForm.attackPaths.unknownSource'));
                  const objectifName = path.objectifVise || t('workshop4.operationalScenarios.measureForm.attackPaths.unspecifiedObjective');

                  return (
                    <div key={path.id}
  onClick={() => handleAttackPathToggle(path.id, !formData.attackPaths.includes(path.id))}
  className={`p-2 border rounded-lg transition-all cursor-pointer ${formData.attackPaths.includes(path.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-blue-300'
                      }`}>
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          id={`path-${path.id}`}
                          value={path.id}
                          checked={formData.attackPaths.includes(path.id)}
                          onChange={handleAttackPathChange}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 mt-0.5 text-blue-600 rounded"
                        />
                        <div className="flex-grow min-w-0">
                          {/* Header with references */}
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            {path.strategicScenarioReference ? (
                              <div className="flex items-center text-xs font-bold rounded overflow-hidden border border-purple-200">
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 border-r border-purple-200">
                                  {path.strategicScenarioReference}
                                </span>
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800">
                                  {path.referenceCode}
                                </span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                {path.referenceCode}
                              </span>
                            )}
                            <label htmlFor={`path-${path.id}`} className="text-xs font-bold text-gray-700 cursor-pointer truncate">
                              {path.type === 'direct'
  ? t('workshop4.operationalScenarios.measureForm.attackPaths.direct')
  : path.stakeholderName
  ? t('workshop4.operationalScenarios.measureForm.attackPaths.viaStakeholder', { stakeholderName: path.stakeholderName })
  : t('workshop4.operationalScenarios.measureForm.attackPaths.reference')}
                            </label>
                          </div>
                          {/* Compact details grid */}
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500 font-semibold">{t('workshop4.operationalScenarios.measureForm.attackPaths.source')}:</span>
                              <span className="text-red-700 font-medium truncate">{sourceName}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500 font-semibold">{t('workshop4.operationalScenarios.measureForm.attackPaths.objective')}:</span>
                              <span className="text-green-700 font-medium truncate">{objectifName}</span>
                            </div>
                            {path.businessValueName && (
                              <div className="col-span-2 flex items-center gap-1">
                                <span className="text-gray-500 font-semibold">{t('workshop4.operationalScenarios.measureForm.attackPaths.businessValueLabel')}:</span>
                                <span className="text-blue-700 font-medium truncate">{path.businessValueName}</span>
                              </div>
                            )}
                            {path.stakeholderName && (
                              <div className="col-span-2 flex items-center gap-1">
                                <span className="text-gray-500 font-semibold flex-shrink-0">{t('workshop4.operationalScenarios.measureForm.attackPaths.stakeholderLabel')}:</span>
                                <span className="text-yellow-800 font-medium truncate">{path.stakeholderName}</span>
                              </div>
                            )}
                            {path.stakeholders && path.stakeholders.length > 0 && !path.stakeholderName && (
                              <div className="col-span-2 flex items-start gap-1">
                                <span className="text-gray-500 font-semibold flex-shrink-0">{t('workshop4.operationalScenarios.measureForm.attackPaths.stakeholderLabel')}:</span>
                                <div className="flex flex-wrap gap-1">
                                  {path.stakeholders.map((stakeholder, idx) => (
                                    <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold bg-yellow-50 text-yellow-800 border border-yellow-200">
                                      {typeof stakeholder === 'object' ? stakeholder.name || t('workshop4.operationalScenarios.measureForm.attackPaths.unknownStakeholder') : stakeholder}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {path.supportingAssets && path.supportingAssets.length > 0 && (
                              <div className="col-span-2 flex items-start gap-1">
                                <span className="text-gray-500 font-semibold flex-shrink-0">{t('workshop4.operationalScenarios.measureForm.attackPaths.supportingAssetLabel')}:</span>
                                <div className="flex flex-wrap gap-1">
                                  {path.supportingAssets.map((asset, idx) => (
                                    <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold bg-orange-50 text-orange-800 border border-orange-200">
                                      {asset.name || asset}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Informations de base */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
            {t('workshop4.operationalScenarios.measureForm.sections.basicInfo')}
          </h4>

          <div className="space-y-2">
            {/* Title and Category */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="md:col-span-2">
                <label htmlFor="title" className="block text-xs font-semibold text-gray-700 mb-1">
                  {t('workshop4.operationalScenarios.measureForm.fields.title')} *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder={t('workshop4.operationalScenarios.measureForm.fields.titlePlaceholder')}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-xs font-semibold text-gray-700 mb-1">
                  {t('workshop4.operationalScenarios.measureForm.fields.category')} *
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                >
                  {CATEGORIES.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-xs font-semibold text-gray-700 mb-1">
                {t('workshop4.operationalScenarios.measureForm.fields.description')} *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={2}
                placeholder={t('workshop4.operationalScenarios.measureForm.fields.descriptionPlaceholder')}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Paramètres de mise en œuvre */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
            {t('workshop4.operationalScenarios.measureForm.sections.implementation')}
          </h4>

          <div className="space-y-2">
            {/* Priority, Implementation, Effectiveness */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label htmlFor="priority" className="block text-xs font-semibold text-gray-700 mb-1">
                  {t('workshop4.operationalScenarios.measureForm.fields.priority')} *
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                >
                  {PRIORITIES.map(priority => (
                    <option key={priority.id} value={priority.id}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="implementation" className="block text-xs font-semibold text-gray-700 mb-1">
                  {t('workshop4.operationalScenarios.measureForm.fields.implementationStatus')}
                </label>
                <select
                  id="implementation"
                  name="implementation"
                  value={formData.implementation}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                >
                  {IMPLEMENTATION_STATUSES.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-1 md:col-span-3">
                <h5 className="text-xs font-semibold text-gray-700 mb-1.5">{t('workshop4.operationalScenarios.measureForm.fields.effectiveness')} (0-4)</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {/* Dépendance */}
                  <div>
                    <label htmlFor="dependanceEffectiveness" className="block text-[10px] font-semibold text-gray-600 mb-0.5">
                      Dépendance (Dim.)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="dependanceEffectiveness"
                        name="dependanceEffectiveness"
                        value={formData.dependanceEffectiveness || 0}
                        onChange={handleChange}
                        min={0}
                        max={4}
                        step="0.1"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                      />
                    </div>
                  </div>

                  {/* Pénétration */}
                  <div>
                    <label htmlFor="penetrationEffectiveness" className="block text-[10px] font-semibold text-gray-600 mb-0.5">
                      Pénétration (Dim.)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="penetrationEffectiveness"
                        name="penetrationEffectiveness"
                        value={formData.penetrationEffectiveness || 0}
                        onChange={handleChange}
                        min={0}
                        max={4}
                        step="0.1"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                      />
                    </div>
                  </div>

                  {/* Maturité Cyber */}
                  <div>
                    <label htmlFor="maturiteCyberEffectiveness" className="block text-[10px] font-semibold text-gray-600 mb-0.5">
                      Maturité Cyber (Aug.)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="maturiteCyberEffectiveness"
                        name="maturiteCyberEffectiveness"
                        value={formData.maturiteCyberEffectiveness || 0}
                        onChange={handleChange}
                        min={0}
                        max={4}
                        step="0.1"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                      />
                    </div>
                  </div>

                  {/* Confiance */}
                  <div>
                    <label htmlFor="confianceEffectiveness" className="block text-[10px] font-semibold text-gray-600 mb-0.5">
                      Confiance (Aug.)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="confianceEffectiveness"
                        name="confianceEffectiveness"
                        value={formData.confianceEffectiveness || 0}
                        onChange={handleChange}
                        min={0}
                        max={4}
                        step="0.1"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Responsible Party and Deadline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label htmlFor="responsibleParty" className="block text-xs font-semibold text-gray-700 mb-1">
                  {t('workshop4.operationalScenarios.measureForm.fields.responsibleParty')}
                </label>
                <select
                  id="responsibleParty"
                  name="responsibleParty"
                  value={formData.responsibleParty}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                >
                  <option value="">{t('context.responsiblePersons.selectResponsible') || 'Sélectionner un responsable...'}</option>
                  {responsiblePersonsList.length > 0 ? (
                    responsiblePersonsList.map(person => (
                      <option key={person.id} value={person.name}>
                        {person.name}{person.role ? ` (${person.role})` : ''}
                      </option>
                    ))
                  ) : (
                    <option disabled>{t('context.responsiblePersons.noResponsibles') || 'Aucun responsable défini - Ajoutez-en dans la page Contexte'}</option>
                  )}
                </select>
                {responsiblePersonsList.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <Info size={12} />
                    {t('context.responsiblePersons.addResponsiblesHint') || 'Veuillez ajouter des responsables dans la page Contexte (Atelier 1)'}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="deadline" className="block text-xs font-semibold text-gray-700 mb-1">
                  {t('workshop4.operationalScenarios.measureForm.fields.deadline')}
                </label>
                <input
                  type="date"
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleChange}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Notes additionnelles */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
            <div className="w-2 h-2 bg-gray-500 rounded-full mr-2"></div>
            {t('workshop4.operationalScenarios.measureForm.sections.notes')}
            <span className="ml-2 text-xs font-normal text-gray-500">({t('workshop4.operationalScenarios.measureForm.fields.optional')})</span>
          </h4>

          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={2}
            placeholder={t('workshop4.operationalScenarios.measureForm.fields.notesPlaceholder')}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm transition-all duration-150"
          />
        </div>

        {/* Form actions */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-150"
            >
              {t('workshop4.operationalScenarios.measureForm.buttons.cancel')}
            </button>
            {!measure && (
              <button
                type="button"
                onClick={handleSaveAndAddAnother}
                className="px-4 py-2 bg-white border-2 border-blue-600 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-50 transition-all duration-150"
              >
                {t('workshop4.operationalScenarios.measureForm.buttons.saveAndAddAnother')}
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 shadow-sm hover:shadow-md transition-all duration-150"
            >
              {measure
                ? t('workshop4.operationalScenarios.measureForm.buttons.update')
                : t('workshop4.operationalScenarios.measureForm.buttons.create')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MeasureForm;
